import type { ReactNode } from 'react';
import { cn } from '@tsgecart/ui';
import { AdminSkeleton } from './admin-skeleton.js';
import { AdminMetricTrend } from './admin-metric-trend.js';

export interface AdminStatCardProps {
  label: string;
  value: string;
  icon?: ReactNode;
  trendPercent?: number | null;
  isLoading?: boolean;
  className?: string;
}

/**
 * A single KPI tile. Never receives a raw database record -- callers pass
 * an already-formatted `value` string (currency/count formatting happens
 * once, at the page level) so this component stays purely presentational
 * and reusable for any metric.
 */
export function AdminStatCard({
  label,
  value,
  icon,
  trendPercent,
  isLoading,
  className,
}: AdminStatCardProps) {
  return (
    <div className={cn('rounded-2xl border border-gray-100 bg-white p-5 shadow-sm', className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        {icon && (
          <span className="bg-brand/10 text-brand-foreground flex h-8 w-8 items-center justify-center rounded-full" aria-hidden="true">
            {icon}
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="mt-3">
          <AdminSkeleton className="h-7 w-24" />
        </div>
      ) : (
        <p className="mt-2 text-2xl font-bold tracking-tight text-gray-900">{value}</p>
      )}
      {!isLoading && trendPercent !== undefined && (
        <div className="mt-2">
          <AdminMetricTrend trendPercent={trendPercent} />
        </div>
      )}
    </div>
  );
}
